/**
 * Lives in its OWN module, not in `api.ts`, and that is a testability constraint rather than taste:
 * `api.ts` uses a TypeScript parameter property, which `node --experimental-strip-types` cannot parse,
 * so ANY test importing it dies at load. ⇒ ***LOGIC PLACED IN A FILE THE TEST RUNNER CANNOT LOAD IS
 * UNARMED BY CONSTRUCTION*** — the same lesson GK-s proved for logic inline in a `.svelte` file, one
 * file type over.
 */
import type { Candle } from "./api";

export type LiveCandle = Candle & { forming?: true };
export type FormingCandle = Candle & { forming: true };

/**
 * THE LIVE FORMING BAR — the owner's ruling: the server owns settled history, while a fresh tick
 * may draw the measured window after the newest served bar. The caller owns the prior forming state;
 * this module only folds one tick and never mutates the served bar or the prior.
 */
export interface FormingInput {
  /** The last bar the SERVER sent. Never mutated. */
  bar: Candle | null;
  /** Tick-level last price, already 1e-9-scaled by the route. */
  price: number | null;
  /** Vendor timestamp of THAT price's row, epoch ms. */
  priceMs: number | null;
  /** Whether the price still deserves to be treated as current. */
  fresh: boolean;
  /** Bar width, ms. 1m spine => 60_000. */
  barMs?: number;
  /** Caller-held forming candle, if the tick remains in that same future window. */
  prior?: FormingCandle | null;
}

export function formingBar(input: FormingInput): LiveCandle | null {
  const { bar, price, priceMs, fresh, barMs = 60_000, prior = null } = input;
  if (bar === null) return prior;
  // A newly served candle owns its window, including when the caller still holds the provisional copy.
  if (prior !== null && prior.t <= bar.t) return bar;
  // A null, stale, non-finite, or unusable-grid tick leaves the current render state alone.
  if (
    price === null || priceMs === null || !fresh ||
    !Number.isFinite(price) || !Number.isFinite(priceMs) ||
    !Number.isFinite(barMs) || barMs <= 0
  ) return prior ?? bar;

  const offsetMs = priceMs - bar.t;
  if (offsetMs < 0) return prior ?? bar; // the tick belongs to an already-closed window
  const windowIndex = Math.floor(offsetMs / barMs);
  if (!Number.isSafeInteger(windowIndex)) return prior ?? bar;

  if (windowIndex === 0) {
    // Existing path: only the newest served bar is replaced in the render copy.
    if (prior !== null && prior.t > bar.t) return prior;
    return {
      ...bar,
      h: Math.max(bar.h, price),
      l: Math.min(bar.l, price),
      c: price,
    };
  }

  const formingT = bar.t + windowIndex * barMs;
  if (!Number.isFinite(formingT) || formingT <= bar.t) return prior ?? bar;
  if (prior !== null && prior.t > formingT) return prior;
  if (prior !== null && prior.t === formingT) {
    return {
      ...prior,
      h: Math.max(prior.h, price),
      l: Math.min(prior.l, price),
      c: price,
      forming: true,
    };
  }
  return {
    t: formingT,
    o: price,
    h: price,
    l: price,
    c: price,
    v: null,
    forming: true,
  };
}
